import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  applyLayoutToDocument,
  PHONE_MQ,
  readStoredMobile,
  resolveLayout,
  writeStoredMobile,
  type LayoutMode,
} from "../domain/layout";

type LayoutContextValue = {
  layout: LayoutMode;
  sim: boolean;
  phone: boolean;
};

const LayoutContext = createContext<LayoutContextValue | null>(null);

function viewportIsPhone() {
  return typeof window !== "undefined" && window.matchMedia(PHONE_MQ).matches;
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [params] = useSearchParams();
  const param = params.get("mobile");
  const [stored, setStored] = useState(readStoredMobile);
  const [viewportPhone, setViewportPhone] = useState(viewportIsPhone);

  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const onChange = () => setViewportPhone(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved = useMemo(
    () => resolveLayout({ param, stored, viewportPhone }),
    [param, stored, viewportPhone],
  );

  useEffect(() => {
    if (resolved.store) {
      writeStoredMobile(resolved.store);
      setStored(resolved.store);
    }
    applyLayoutToDocument(resolved.layout, resolved.sim);
  }, [resolved.layout, resolved.sim, resolved.store]);

  const value = useMemo(
    () => ({
      layout: resolved.layout,
      sim: resolved.sim,
      phone: resolved.layout === "phone",
    }),
    [resolved.layout, resolved.sim],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export function useLayout(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayout must be used within LayoutProvider");
  return ctx;
}
